import React from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { productQueries } from "@surgia/api/src/queryStore/admin/product";
import { UpdateTenantProductPayload } from "@surgia/ui/dtos/request";
import { toast } from "sonner";
import { useCustomerView } from "../../hooks/useCustomerView";
import { useUpdateProduct } from "../../hooks/useUpdateProduct";
import ProductHeader from "./ProductHeader";
import ProductFormFields from "./ProductFormFields";
import ProductActions from "./ProductActions";
import VariantCard from "./VariantCard";
import { ProductCardFinalProps, ProductFormData } from "./types";

/**
 * Main ProductCardFinal component that combines parent product and variant management
 * in a clean, organized structure following the established design patterns.
 */
const ProductCardFinal: React.FC<ProductCardFinalProps> = ({
  productId,
  productName,
  productImage,
  initialPrice,
  initialMinQuantity,
  initialMaxQuantity,
  initialCustomerProductId,
  initialCustomerProductName,
  expandedProducts,
  expandedVariants,
  onToggleProductExpansion,
  onToggleVariantExpansion,
  isProductDetailsPage,
  showOnlyCurrentlyAssignedVariants,
  tenantId,
  currencyCode,
}) => {
  const isExpanded = expandedProducts.has(productId);

  // Fetch variants for this product - now always fetched internally
  const { data: productVariationsResponse, isLoading: isLoadingVariations } =
    useQuery({
      ...productQueries.queries.productVariations(
        productId || "",
        tenantId || ""
      ),
      enabled: !!productId && !!tenantId,
    });

  // Find the current product data from productVariationsResponse for fresh customer view values
  const currentProductData = productVariationsResponse?.find(
    (item: any) => item.id.toString() === productId
  );

  // Use the useUpdateProduct hook
  const { updateProduct, isUpdating } = useUpdateProduct({
    parentProductId: productId,
    tenantId: tenantId || "",
  });

  // React Hook Form setup for parent product
  const {
    register,
    formState: { isDirty, errors },
    watch,
    getValues,
    reset,
  } = useForm<ProductFormData & { customerProductName?: string }>({
    defaultValues: {
      price:
        currentProductData?.tenant_product_prices?.[0]?.price ??
        initialPrice ??
        null,
      minQuantity:
        currentProductData?.minimum_quantity ?? initialMinQuantity ?? null,
      maxQuantity:
        currentProductData?.maximum_quantity ?? initialMaxQuantity ?? null,
      customerProductId:
        currentProductData?.tenant_product_sku ||
        initialCustomerProductId ||
        "",
      customerProductName:
        currentProductData?.tenant_product_name ||
        initialCustomerProductName ||
        "",
    },
    mode: "onChange",
  });

  const currentFormValues = watch();

  // Custom hook for parent product customer view
  // Pass undefined when data is loading to avoid showing false initially
  const {
    customerView: parentCustomerView,
    toggleCustomerView: toggleParentCustomerView,
    isDisabled: isParentCustomerViewDisabled,
    isLoading: isParentCustomerViewLoading,
  } = useCustomerView({
    initialCustomerView: isLoadingVariations
      ? undefined
      : currentProductData?.customer_view,
    productId: productId,
    isDisabled:
      !currentProductData?.tenant_product_prices?.[0]?.price ||
      currentProductData?.minimum_quantity === null ||
      currentProductData?.maximum_quantity === null,
    tenantId: tenantId || "",
    parentProductId: productId,
  });

  // Handle parent product save operation
  const handleParentSave = async () => {
    const formData = {
      price: Number(getValues().price) || null,
      minQuantity: Number(getValues().minQuantity) || null,
      maxQuantity: Number(getValues().maxQuantity) || null,
      customerProductId: getValues().customerProductId || "",
      customerProductName: getValues().customerProductName || "",
    };

    // Use the productId directly as it's the tenant product ID
    const tenantProductId = productId;
    if (!formData.minQuantity || !formData.maxQuantity || !formData.price) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }

    if (!tenantProductId) {
      toast.error("Tenant Product ID is missing. Cannot save product.");
      console.error("Error: tenantProductId is undefined");
      return;
    }

    if (!currencyCode) {
      toast.error("Currency information is not available. Please refresh the page.");
      return;
    }

    const payload: UpdateTenantProductPayload = {
      minimumQuantity: Number(formData.minQuantity),
      maximumQuantity: Number(formData.maxQuantity),
      tenantProductName: formData.customerProductName || "",
      tenantProductSku: formData.customerProductId || "",
      productPrices: [
        {
          price: Number(formData.price),
          currency: currencyCode,
        },
      ],
    };

    try {
      await updateProduct({
        tenantProductId: tenantProductId,
        payload,
      });

      // Form data is now managed by React Hook Form and fresh data comes from API
      reset(formData);
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };

  // Check parent form validity
  const isParentFormValid =
    currentFormValues.minQuantity !== null &&
    currentFormValues.maxQuantity !== null &&
    (!currentFormValues.minQuantity ||
      !currentFormValues.maxQuantity ||
      Number(currentFormValues.minQuantity) <=
        Number(currentFormValues.maxQuantity));

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Product Header */}
      <ProductHeader
        productName={productName}
        productImage={productImage}
        isParent={true}
        isExpanded={isExpanded}
        customerView={parentCustomerView}
        isCustomerViewDisabled={isParentCustomerViewDisabled}
        isProductDetailsPage={isProductDetailsPage}
        onToggleExpansion={() => onToggleProductExpansion(productId)}
      />

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 py-4 border-t border-gray-200 space-y-4">
          {/* Parent Product Form */}
          <ProductFormFields
            register={register}
            errors={errors}
            showCustomerProductName={true}
          />

          {/* Parent Product Actions */}
          <ProductActions
            customerView={parentCustomerView}
            isCustomerViewDisabled={isParentCustomerViewDisabled}
            isCustomerViewLoading={isParentCustomerViewLoading}
            onToggleCustomerView={toggleParentCustomerView}
            onCancel={() => reset()}
            onSave={handleParentSave}
            isSaving={isUpdating}
            isDirty={isDirty}
            hasErrors={Object.keys(errors).length > 0}
            isFormValid={isParentFormValid}
          />

          {/* Variants Section */}
          {/* Since we're on ProductDetails page, this is always a parent product */}
          {(() => {
            return isLoadingVariations || productVariationsResponse?.length > 0;
          })() && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Product Variants
              </h4>
              <div className="space-y-3">
                {isLoadingVariations ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="bg-gray-50 rounded-lg border border-gray-200 p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
                            <div className="space-y-2">
                              <div className="h-4 bg-gray-200 rounded animate-pulse w-32" />
                              <div className="h-3 bg-gray-200 rounded animate-pulse w-24" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="h-6 bg-gray-200 rounded animate-pulse w-16" />
                            <div className="w-4 h-4 bg-gray-200 rounded animate-pulse" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  (() => {
                    return productVariationsResponse
                      ?.filter((variant: any) => {
                        // Filter out the parent product itself
                        return (
                          variant?.catalog_info?.id?.toString() !==
                          productVariationsResponse?.[0]?.product_id?.toString()
                        );
                      })
                      .map((variant: any) => {
                        const featureDisplay =
                          variant?.catalog_info?.feature_values
                            ?.map(
                              (feature: any) =>
                                feature.featureVariant.variant_name
                            )
                            .join(", ") || "";

                        const variantData = {
                          id: variant.id.toString(),
                          name: featureDisplay,
                          price:
                            variant.tenant_product_prices[0]?.price ||
                            variant.catalog_info?.price ||
                            null,
                          minQuantity: variant.minimum_quantity || null,
                          maxQuantity: variant.maximum_quantity || null,
                          customerProductId: variant.tenant_product_sku || "",
                          isParent: false,
                          tenantProductId: variant.id.toString(),
                          productId: variant.catalog_info?.id,
                          isVisible: variant.currently_assigned || false,
                          tenantInfoId: variant.id.toString(),
                          hasInitialValues: false,
                          hasChanges: false,
                          customer_view: variant.customer_view,
                          price_assigned: variant.price_assigned,
                          minimum_quantity: variant.minimum_quantity,
                          maximum_quantity: variant.maximum_quantity,
                          tenant_product_sku: variant.tenant_product_sku,
                          image:
                            variant?.catalog_info?.product_images?.[0]
                              ?.image_path,
                        };

                        // Simple approach: use currently_assigned directly
                        if (!variant.currently_assigned) {
                          return null;
                        }

                        return (
                          <VariantCard
                            key={`${variant.id}-${variant.tenant_product_prices?.[0]?.price}-${variant.minQuantity}-${variant.maxQuantity}`}
                            tenantId={tenantId || ""}
                            variant={variantData} // Pass the normalized variant data
                            product={productVariationsResponse?.[0]}
                            onToggleExpansion={onToggleVariantExpansion}
                            expandedVariants={expandedVariants}
                            isProductDetailsPage={isProductDetailsPage}
                            showOnlyCurrentlyAssignedVariants={
                              showOnlyCurrentlyAssignedVariants
                            }
                            currencyCode={currencyCode} 
                          />
                        );
                      })
                      .filter(Boolean);
                  })()
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Export the main component as default
export default ProductCardFinal;

// Export all types
export type {
  ProductConfiguration,
  ProductCardFinalProps,
  ProductFormData,
  ProductHeaderProps,
  ProductFormFieldsProps,
  ProductActionsProps,
  VariantCardProps,
} from "./types";

// Export individual components for potential reuse
export { default as ProductHeader } from "./ProductHeader";
export { default as ProductFormFields } from "./ProductFormFields";
export { default as ProductActions } from "./ProductActions";
export { default as VariantCard } from "./VariantCard";
